import is from '@sindresorhus/is';
import type { FunctionComponent, PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useRef } from 'react';
import type { StoreApi } from 'zustand';

import { ApplicationContext } from '~/shared/context/context';

import { useApplicationFetchers } from '../../fetchers/use-application-fetchers';
import { createLoadSkillsBySearchTermRequest } from './api/load-skills-by-search-term-request';
import type { FeedbackDialogStore } from './feedback-dialog-store';
import { createFeedbackDialogStore } from './feedback-dialog-store';
import { createTranslatedFeedbackDialogLabels } from './feedback-dialog-store-labels';
import type { FeedbackEmitterDependencies } from './feedback-emitter';
import { createFeedbackEmitter } from './feedback-emitter';

export const FeedbackDialogContext = createContext<StoreApi<FeedbackDialogStore> | null>(null);

export const FeedbackDialogContextProvider: FunctionComponent<PropsWithChildren> = (properties) => {
    const { actions, applicationState, translate, validatingCobrainerApiFetcher } = useContext(ApplicationContext);

    const storeRef = useRef<StoreApi<FeedbackDialogStore>>(undefined);

    const { changeSkillFeedback, reportSkillFeedback, requestSkillFeedback } = useApplicationFetchers();

    if (is.undefined(storeRef.current)) {
        const sendRequestSkill: FeedbackEmitterDependencies['sendRequestSkill'] = async (params) => {
            return requestSkillFeedback(params);
        };

        const sendChangeSkill: FeedbackEmitterDependencies['sendChangeSkill'] = async (params) => {
            return changeSkillFeedback(params);
        };

        const sendReportSkill: FeedbackEmitterDependencies['sendReportSkill'] = async (params) => {
            return reportSkillFeedback(params);
        };

        storeRef.current = createFeedbackDialogStore({
            feedbackEmitter: createFeedbackEmitter({
                loadSkillsBySearchTerm: createLoadSkillsBySearchTermRequest(validatingCobrainerApiFetcher),
                sendChangeSkill,
                sendReportSkill,
                sendRequestSkill,
            }),
            getFeedbackDialogStoreLabels: createTranslatedFeedbackDialogLabels(translate),
            mailTo: actions.mailTo,
            selectedLanguage: applicationState.language.code,
        });
    }

    // biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration
    useEffect(() => {
        const store = storeRef.current;
        if (is.nullOrUndefined(store)) {
            return;
        }

        store.getState().updateStoreLabelsTranslations();
    }, [applicationState.language.code]);

    return (
        <FeedbackDialogContext.Provider value={storeRef.current}>{properties.children}</FeedbackDialogContext.Provider>
    );
};
